import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  FiBriefcase,
  FiCalendar,
  FiEye,
  FiDownload,
  FiSearch,
  FiX,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiMapPin,
  FiRefreshCw,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import { useAuth } from "../contexts/AuthContext";
import { extractList } from "../utils/api";
import { formatDate, timeAgo, statusLabel, statusTone } from "../utils/format";
import { downloadSpreadsheet } from "../utils/download";

const STATUS_TABS = [
  { status: "TODOS", label: "Todas" },
  { status: "PENDENTE", label: "Aguardando" },
  { status: "APROVADO", label: "Aprovadas" },
  { status: "REJEITADO", label: "Reprovadas" },
];

const COMPANY_TYPES = [
  { value: "INDUSTRIA", label: "Indústria" },
  { value: "FORNECEDOR_PRODUTOR", label: "Fornecedor / Produtor" },
  { value: "DISTRIBUIDOR", label: "Distribuidor" },
];

const STATUS_ICON = {
  APROVADO: FiCheckCircle,
  PENDENTE: FiClock,
  REJEITADO: FiXCircle,
};

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const companyTypeLabel = (value) =>
  COMPANY_TYPES.find((type) => type.value === value)?.label || "";

const startOfDay = (date) =>
  date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) : null;

const endOfDay = (date) =>
  date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59) : null;

const CompaniesPage = () => {
  const { user } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const datePickerRef = useRef(null);

  const canAccessCompanies = ["POWER_USER", "ADMIN"].includes(user?.role);

  const fetchCompanies = async () => {
    if (!canAccessCompanies) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get("/companies", {
        params: { itemsPerPage: -1 },
      });
      setCompanies(extractList(response));
    } catch (error) {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessCompanies]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSpreadsheet(
        "/companies/export",
        {
          ...(statusFilter !== "TODOS" ? { status: statusFilter } : {}),
          ...(searchTerm.trim() ? { q: searchTerm.trim() } : {}),
        },
        "empresas"
      );
    } catch (error) {
      window.alert("Não foi possível exportar as empresas. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const dateRangeLabel = useMemo(() => {
    if (dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString("pt-BR")} - ${dateRange.to.toLocaleDateString("pt-BR")}`;
    }
    if (dateRange.from) return `De ${dateRange.from.toLocaleDateString("pt-BR")}`;
    return "Selecionar intervalo";
  }, [dateRange.from, dateRange.to]);

  // Contadores por status vêm da lista completa — o endpoint já devolve tudo.
  const counts = useMemo(
    () =>
      companies.reduce(
        (acc, company) => {
          const status = company?.statusVerificacao;
          if (status && acc[status] !== undefined) acc[status] += 1;
          acc.TODOS += 1;
          return acc;
        },
        { TODOS: 0, PENDENTE: 0, APROVADO: 0, REJEITADO: 0 }
      ),
    [companies]
  );

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const fromDate = startOfDay(dateRange.from);
    const toDate = endOfDay(dateRange.to);

    return companies.filter((company) => {
      const haystack = [
        company?.nomeFantasia,
        company?.razaoSocial,
        company?.name,
        company?.emailCorporativo,
        company?.email,
        company?.cnpj,
        company?.whatsapp,
        company?.enderecoCidade,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !term || haystack.includes(term);
      const matchesStatus =
        statusFilter === "TODOS" || company?.statusVerificacao === statusFilter;
      const matchesType = !typeFilter || company?.tipoEmpresa === typeFilter;

      const createdAt = company?.createdAt ? new Date(company.createdAt) : null;
      const matchesFrom = !fromDate || (createdAt && createdAt >= fromDate);
      const matchesTo = !toDate || (createdAt && createdAt <= toDate);

      return matchesSearch && matchesStatus && matchesType && matchesFrom && matchesTo;
    });
  }, [companies, searchTerm, statusFilter, typeFilter, dateRange.from, dateRange.to]);

  const activeChips = [
    searchTerm.trim() && {
      key: "search",
      label: `Busca: "${searchTerm.trim()}"`,
      clear: () => setSearchTerm(""),
    },
    typeFilter && {
      key: "type",
      label: `Tipo: ${companyTypeLabel(typeFilter)}`,
      clear: () => setTypeFilter(""),
    },
    (dateRange.from || dateRange.to) && {
      key: "date",
      label: `Cadastro: ${dateRangeLabel}`,
      clear: () => setDateRange({ from: undefined, to: undefined }),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
    setDateRange({ from: undefined, to: undefined });
  };

  if (!canAccessCompanies) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Acesso restrito"
        description="A listagem de empresas está disponível apenas para usuários POWER_USER ou ADMIN."
      />
    );
  }

  const renderStatus = (company) => {
    const status = company?.statusVerificacao;
    if (!status) return <span className="muted">-</span>;

    const Icon = STATUS_ICON[status];

    return (
      <span
        className="badge"
        data-tone={statusTone(status)}
        style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
      >
        {Icon && <Icon size={12} />}
        {status === "REJEITADO" ? "Reprovada" : statusLabel(status)}
      </span>
    );
  };

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Empresas</h2>
          <p>
            Anunciantes cadastrados na plataforma. Aprovar a verificação libera o
            selo de fornecedor verificado na vitrine.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="button button--ghost"
            onClick={fetchCompanies}
            disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiRefreshCw size={16} />
            Atualizar
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={handleExport}
            disabled={exporting}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiDownload size={16} />
            {exporting ? "Gerando Excel..." : "Exportar Excel"}
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="plist-toolbar">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.status}
              type="button"
              className={`plist-tab${statusFilter === tab.status ? " is-active" : ""}${
                tab.status === "PENDENTE" ? " plist-tab--alert" : ""
              }`}
              onClick={() => setStatusFilter(tab.status)}
            >
              {tab.label}
              <span className="plist-tab__count">{counts[tab.status] || 0}</span>
            </button>
          ))}
        </div>

        <div className="plist-filters">
          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="companies-search">
              Buscar
            </label>
            <div className="plist-search">
              <FiSearch className="plist-search__icon" size={16} />
              <input
                id="companies-search"
                className="input"
                type="text"
                placeholder="Nome, CNPJ, e-mail, WhatsApp ou cidade"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="plist-search__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Limpar busca"
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="companies-type">
              Tipo de empresa
            </label>
            <select
              id="companies-type"
              className="select"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="">Todos</option>
              {COMPANY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Período de cadastro</label>
            <div className="plist-daterange" ref={datePickerRef}>
              <button
                type="button"
                className="input"
                onClick={() => setIsDatePickerOpen((prev) => !prev)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <span>{dateRangeLabel}</span>
                <FiCalendar />
              </button>

              {isDatePickerOpen && (
                <div className="plist-daterange__popover">
                  <DayPicker
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) =>
                      setDateRange(range || { from: undefined, to: undefined })
                    }
                    numberOfMonths={1}
                  />
                  <button
                    type="button"
                    className="button button--ghost"
                    style={{ width: "100%", marginTop: "8px" }}
                    onClick={() => {
                      setDateRange({ from: undefined, to: undefined });
                      setIsDatePickerOpen(false);
                    }}
                  >
                    Limpar período
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="plist-chips">
            <span>Filtros:</span>
            {activeChips.map((chip) => (
              <span key={chip.key} className="plist-chip">
                {chip.label}
                <button
                  type="button"
                  onClick={chip.clear}
                  aria-label={`Remover ${chip.label}`}
                >
                  <FiX size={12} />
                </button>
              </span>
            ))}
            <button type="button" className="plist-chips__reset" onClick={clearAllFilters}>
              Limpar tudo
            </button>
          </div>
        )}

        <div className="muted" style={{ marginBottom: "0.75rem", fontSize: "0.84rem" }}>
          {loading
            ? "Carregando empresas..."
            : `Exibindo ${filteredCompanies.length} de ${companies.length} empresa(s)`}
        </div>

        {loading ? (
          <div className="plist-scroll">
            <table className="plist-table">
              <tbody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <div
                          className="plist-skeleton"
                          style={{ width: 38, height: 38, borderRadius: "50%" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="plist-skeleton" style={{ width: "58%" }} />
                          <div
                            className="plist-skeleton"
                            style={{ width: "32%", height: 10, marginTop: 8 }}
                          />
                        </div>
                      </div>
                    </td>
                    <td><div className="plist-skeleton" style={{ width: "72%" }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 110 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 90 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 100 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : companies.length === 0 ? (
          <NoData
            title="Nenhuma empresa encontrada"
            description="Não há empresas para listar no momento."
          />
        ) : filteredCompanies.length === 0 ? (
          <NoData
            title="Nenhum resultado para os filtros"
            description="Ajuste a busca, o status ou o período selecionado."
          />
        ) : (
          <>
            <div className="companies-table-wrap">
              <div className="plist-scroll">
                <table className="plist-table">
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>Contato</th>
                      <th>Localização</th>
                      <th>Status</th>
                      <th>Cadastro</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((company) => {
                      const displayName =
                        company?.nomeFantasia || company?.razaoSocial || company?.name || "-";
                      const legalName =
                        company?.razaoSocial && company?.razaoSocial !== displayName
                          ? company.razaoSocial
                          : "";
                      const city = [company?.enderecoCidade, company?.enderecoEstado]
                        .filter(Boolean)
                        .join("/");

                      return (
                        <tr key={company.id}>
                          <td>
                            <div className="plist-seller">
                              <span className="plist-avatar plist-avatar--lg">
                                {company?.logoUrl ? (
                                  <img src={company.logoUrl} alt={displayName} />
                                ) : (
                                  initialsOf(displayName)
                                )}
                              </span>
                              <div>
                                <div className="plist-seller__name">{displayName}</div>
                                <div className="plist-product__meta">
                                  {company?.cnpj && (
                                    <span className="plist-tag">{company.cnpj}</span>
                                  )}
                                  {company?.tipoEmpresa && (
                                    <span>{companyTypeLabel(company.tipoEmpresa)}</span>
                                  )}
                                </div>
                                {legalName && (
                                  <div
                                    className="muted"
                                    style={{ fontSize: "0.74rem", marginTop: 3 }}
                                  >
                                    {legalName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td>
                            <span className="plist-contact">
                              {company?.emailCorporativo || company?.email || "-"}
                              {company?.whatsapp && <span>{company.whatsapp}</span>}
                            </span>
                          </td>

                          <td>
                            {city ? (
                              <span
                                className="plist-tag"
                                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                              >
                                <FiMapPin size={11} />
                                {city}
                              </span>
                            ) : (
                              <span className="muted">-</span>
                            )}
                          </td>

                          <td>{renderStatus(company)}</td>

                          <td>
                            <div className="plist-date" title={formatDate(company?.createdAt)}>
                              {company?.createdAt ? timeAgo(company.createdAt) : "-"}
                              <span>{formatDate(company?.createdAt)}</span>
                            </div>
                          </td>

                          <td>
                            <div className="plist-actions">
                              <Link className="plist-link" to={`/empresas/${company.id}`}>
                                <FiEye size={14} />
                                Ver detalhes
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="companies-mobile-list">
              {filteredCompanies.map((company) => {
                const displayName =
                  company?.nomeFantasia || company?.razaoSocial || company?.name || "-";
                const city = [company?.enderecoCidade, company?.enderecoEstado]
                  .filter(Boolean)
                  .join("/");

                return (
                  <div key={company.id} className="company-mobile-card">
                    <div className="company-mobile-card__header">
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span className="plist-avatar plist-avatar--lg">
                          {company?.logoUrl ? (
                            <img src={company.logoUrl} alt={displayName} />
                          ) : (
                            initialsOf(displayName)
                          )}
                        </span>
                        <div>
                          <div className="company-mobile-card__name">{displayName}</div>
                          <div className="company-mobile-card__cnpj">
                            {company?.cnpj || "-"}
                          </div>
                        </div>
                      </div>
                      {renderStatus(company)}
                    </div>

                    <div className="company-mobile-card__meta">
                      <span className="company-mobile-card__meta-item">
                        {company?.emailCorporativo || company?.email || "-"}
                      </span>
                      {city && (
                        <span className="company-mobile-card__meta-item">
                          <FiMapPin size={12} />
                          {city}
                        </span>
                      )}
                    </div>

                    <div className="company-mobile-card__footer">
                      <div className="company-mobile-card__date">
                        <span>{formatDate(company?.createdAt)}</span>
                        {company?.createdAt && (
                          <span style={{ display: "block" }}>{timeAgo(company.createdAt)}</span>
                        )}
                      </div>
                      <Link className="plist-link" to={`/empresas/${company.id}`}>
                        <FiEye size={14} />
                        Ver detalhes
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompaniesPage;
